import React, { useEffect, useRef, useState } from "react";
import { Moon, X, Check } from "lucide-react";
import { claimPlayback } from "../lib/playbackBus";

const PRESETS = [
  { label: "15 min", minutes: 15 },
  { label: "30 min", minutes: 30 },
  { label: "45 min", minutes: 45 },
  { label: "1 ora", minutes: 60 },
  { label: "1 ora e mezza", minutes: 90 },
  { label: "2 ore", minutes: 120 },
];

const STORAGE_KEY = "mt_sleep_until";

function loadStored() {
  try {
    const ts = Number(localStorage.getItem(STORAGE_KEY) || 0);
    if (ts && ts > Date.now()) return ts;
  } catch (_e) {
    /* ignore */
  }
  return null;
}

function formatRemaining(ms) {
  if (ms <= 0) return "0:00";
  const total = Math.ceil(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Sleep timer: floating chip in the bottom-left that lets the user schedule
 * an automatic shutdown of both Radio and TV playback. When the countdown
 * reaches zero, `claimPlayback("sleep")` is dispatched, causing both players
 * (which listen for non-"tv" / non-"radio" claims) to pause themselves.
 */
export const SleepTimer = () => {
  const [until, setUntil] = useState(() => loadStored());
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [justFired, setJustFired] = useState(false);
  const tickRef = useRef(null);

  // 1Hz tick while a timer is armed (or briefly after firing)
  useEffect(() => {
    if (!until && !justFired) {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
      return undefined;
    }
    if (tickRef.current) return undefined;
    tickRef.current = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
    };
  }, [until, justFired]);

  // Fire on expiry
  useEffect(() => {
    if (!until) return;
    if (now >= until) {
      claimPlayback("sleep");
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (_e) {
        /* ignore */
      }
      setUntil(null);
      setJustFired(true);
      const t = setTimeout(() => setJustFired(false), 4500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [now, until]);

  const arm = (minutes) => {
    const ts = Date.now() + minutes * 60 * 1000;
    setUntil(ts);
    setOpen(false);
    setJustFired(false);
    try {
      localStorage.setItem(STORAGE_KEY, String(ts));
    } catch (_e) {
      /* ignore */
    }
  };

  const cancel = () => {
    setUntil(null);
    setOpen(false);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (_e) {
      /* ignore */
    }
  };

  const remaining = until ? Math.max(0, until - now) : 0;
  const armed = Boolean(until) && remaining > 0;

  return (
    <div
      className="fixed left-4 sm:left-6 bottom-4 sm:bottom-6 z-40"
      data-testid="sleep-timer"
    >
      {justFired && (
        <div
          data-testid="sleep-timer-fired"
          className="absolute left-0 bottom-full mb-2 inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-semibold shadow-xl whitespace-nowrap"
          style={{
            background: "rgba(20,17,13,0.95)",
            color: "var(--mt-cream)",
            border: "1px solid rgba(212,160,91,0.45)",
            backdropFilter: "blur(10px)",
          }}
        >
          <Moon size={13} strokeWidth={2.2} />
          Buonanotte 🌙
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="sleep-timer-btn"
        aria-label={
          armed
            ? `Timer di spegnimento: ${formatRemaining(remaining)}`
            : "Imposta timer di spegnimento"
        }
        className="mt-btn inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold shadow-xl tabular-nums"
        style={{
          background: armed
            ? "rgba(212,160,91,0.20)"
            : "rgba(20,17,13,0.92)",
          color: armed ? "var(--mt-cream)" : "var(--mt-amber-bright)",
          border: armed
            ? "1px solid rgba(212,160,91,0.55)"
            : "1px solid rgba(212,160,91,0.40)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Moon size={14} strokeWidth={2.2} />
        {armed ? formatRemaining(remaining) : "Timer"}
      </button>

      {open && (
        <div
          className="absolute left-0 bottom-full mb-2 w-64 rounded-2xl overflow-hidden"
          data-testid="sleep-timer-popover"
          style={{
            background:
              "linear-gradient(180deg, rgba(28,24,19,0.98) 0%, rgba(20,17,13,0.98) 100%)",
            border: "1px solid rgba(212,160,91,0.30)",
            boxShadow: "0 18px 50px rgba(0,0,0,0.6)",
            backdropFilter: "blur(14px)",
          }}
        >
          <div className="px-4 pt-4 pb-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Moon
                size={15}
                strokeWidth={2.2}
                style={{ color: "var(--mt-amber-bright)" }}
              />
              <div
                className="font-display text-base"
                style={{ color: "var(--mt-cream)" }}
              >
                Spegni fra…
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Chiudi"
              className="mt-btn w-7 h-7 rounded-full inline-flex items-center justify-center"
              style={{
                color: "var(--mt-text-muted)",
                background: "rgba(245,239,228,0.06)",
              }}
            >
              <X size={12} strokeWidth={2.2} />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-1.5 px-3 pb-3">
            {PRESETS.map((p) => (
              <button
                key={p.minutes}
                type="button"
                onClick={() => arm(p.minutes)}
                data-testid={`sleep-preset-${p.minutes}`}
                className="mt-btn px-3 py-2 rounded-lg text-xs font-semibold text-left"
                style={{
                  background: "rgba(245,239,228,0.04)",
                  color: "var(--mt-cream)",
                  border: "1px solid rgba(245,239,228,0.08)",
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
          {armed && (
            <button
              type="button"
              onClick={cancel}
              data-testid="sleep-timer-cancel"
              className="mt-btn w-full px-4 py-2.5 text-xs font-semibold inline-flex items-center justify-center gap-1.5 border-t"
              style={{
                background: "rgba(184,85,48,0.10)",
                color: "#f4a98a",
                borderColor: "rgba(245,239,228,0.06)",
              }}
            >
              <X size={13} strokeWidth={2.4} />
              Annulla timer ({formatRemaining(remaining)})
            </button>
          )}
          {!armed && (
            <div
              className="px-4 py-2.5 text-[10px] uppercase tracking-[0.22em] border-t"
              style={{
                color: "var(--mt-text-muted)",
                borderColor: "rgba(245,239,228,0.06)",
              }}
            >
              Allo scadere, radio e TV si fermano
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SleepTimer;
